<template>
  <div class="tag-mgmt">
    <div class="tag-mgmt__scroll">
      <div v-if="!tags.length && !adding" class="tag-mgmt__empty">No tags yet. Click "+ Add Tag" below.</div>
      <div
        v-for="(tag, index) in tags"
        :key="tag.key + index"
        class="tag-mgmt__row"
        @mouseenter="hoverIndex = index"
        @mouseleave="hoverIndex = -1">
        <v-menu :close-on-content-click="true">
          <template #activator="{ props: menuProps }">
            <button v-bind="menuProps" class="tag-mgmt__dot tag-mgmt__dot--clickable" type="button">
              <span class="tag-mgmt__dot-inner" :style="{ background: TAG_COLORS[tag.color] || TAG_COLORS.grey }" />
            </button>
          </template>
          <div class="tag-mgmt__palette">
            <button
              v-for="(hex, name) in TAG_COLORS"
              :key="name"
              type="button"
              class="tag-mgmt__palette-swatch"
              :class="{ 'tag-mgmt__palette-swatch--active': tag.color === name }"
              :style="{ background: hex }"
              @click="setColor(tag, name)" />
          </div>
        </v-menu>
        <input
          v-model="tag.label"
          class="tag-mgmt__input"
          placeholder="Tag label"
          @input="tag.dirty = true"
          @blur="autoKey(tag)" />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="x-small"
          class="tag-mgmt__delete"
          :class="{ 'tag-mgmt__delete--visible': hoverIndex === index }"
          @click="removeTag(index)" />
      </div>

      <!-- Inline add row -->
      <div v-if="adding" class="tag-mgmt__row tag-mgmt__row--adding">
        <span class="tag-mgmt__dot">
          <span class="tag-mgmt__dot-inner" :style="{ background: TAG_COLORS[newTag.color] || TAG_COLORS.grey }" />
        </span>
        <input
          ref="newLabelInput"
          v-model="newTag.label"
          class="tag-mgmt__input"
          placeholder="New tag label"
          @keyup.enter="confirmAdd"
          @keyup.escape="cancelAdd"
          @blur="confirmAdd" />
      </div>

      <button type="button" class="tag-mgmt__add-btn" :disabled="!loaded" @click="startAdd">+ Add Tag</button>
    </div>

    <!-- Save bar -->
    <div class="tag-mgmt__save-bar">
      <v-btn variant="text" size="small" @click="resetToDefaults">Reset to Defaults</v-btn>
      <transition name="tag-mgmt-fade">
        <span v-if="error" class="tag-mgmt__msg tag-mgmt__msg--error">{{ error }}</span>
        <span v-else-if="successMsg" class="tag-mgmt__msg tag-mgmt__msg--success">{{ successMsg }}</span>
      </transition>
      <v-spacer />
      <v-btn variant="flat" size="small" color="primary" :loading="saving" :disabled="!hasChanges" @click="save">
        Save
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { TAG_COLORS } from "@/constants/tagColors";
import { ref, computed, watch, nextTick } from "vue";
import { useRequirementsStore } from "@/stores";
import { settingsService } from "@/services/settingsService";
import { SCOPE, toBackendScope, isValidScope } from "@/constants/scope";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: isValidScope,
  },
});

const requirementsStore = useRequirementsStore();
const { saving, error, successMsg, run: runSave } = useSettingsSaveFlow();
const loaded = ref(false);
const adding = ref(false);
const hoverIndex = ref(-1);
const newLabelInput = ref(null);
const newTag = ref({ label: "", color: "blue", key: "" });

const intakeTags = ref([]);
const priorAuthTags = ref([]);
const intakeInitial = ref([]);
const priorAuthInitial = ref([]);

// Map of UI scope → ref for per-scope state. Single point of truth for dispatch;
// addresses review feedback about the repeated `scope === "intake" ? a : b` ternary.
const tagsByScope = { [SCOPE.PA]: priorAuthTags, [SCOPE.INTAKE]: intakeTags };
const initialByScope = { [SCOPE.PA]: priorAuthInitial, [SCOPE.INTAKE]: intakeInitial };
const getTags = () => tagsByScope[props.scope];
const getInitial = () => initialByScope[props.scope];

const tags = computed(() => getTags().value);

const hasChanges = computed(() => {
  const current = getTags().value;
  const initial = getInitial().value;
  return JSON.stringify(current.map(({ key, label, color }) => ({ key, label, color }))) !== JSON.stringify(initial);
});

function labelToKey(label) {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function autoKey(tag) {
  if (tag.dirty && tag.label) {
    tag.key = labelToKey(tag.label);
  }
}

function setColor(tag, color) {
  tag.color = color;
  tag.dirty = true;
}

function startAdd() {
  if (adding.value) {
    nextTick(() => newLabelInput.value?.focus());
    return;
  }
  adding.value = true;
  newTag.value = { label: "", color: "blue", key: "" };
  nextTick(() => newLabelInput.value?.focus());
}

function confirmAdd() {
  if (!adding.value) return;
  const label = newTag.value.label.trim();
  if (!label) {
    adding.value = false;
    return;
  }
  const key = labelToKey(label);
  if (!key) {
    adding.value = false;
    return;
  }

  const target = getTags();
  if (target.value.some((t) => t.key === key)) {
    error.value = `Tag "${key}" already exists.`;
    adding.value = false;
    newTag.value = { label: "", color: "blue", key: "" };
    return;
  }

  target.value.push({ key, label, color: newTag.value.color, dirty: true });
  adding.value = false;
  newTag.value = { label: "", color: "blue", key: "" };
  error.value = "";
}

function cancelAdd() {
  adding.value = false;
  newTag.value = { label: "", color: "blue", key: "" };
}

function removeTag(index) {
  getTags().value.splice(index, 1);
}

async function save() {
  if (adding.value) {
    confirmAdd();
    await nextTick();
  }
  await runSave(
    async () => {
      const backendScope = toBackendScope(props.scope);
      const current = tags.value.map(({ key, label, color }) => ({ key, label, color }));
      const response = await settingsService.updateCustomTags(backendScope, current);
      if (!Array.isArray(response?.tags) || response.tags.length !== current.length) {
        throw new Error("Some tags were rejected by the server. Please review and retry.");
      }
      await requirementsStore.refreshCurrentUserClientConfig();
      await loadTags();
    },
    { successText: "Tags saved.", errorText: "Failed to save tags." }
  );
}

async function resetToDefaults() {
  await runSave(
    async () => {
      await settingsService.resetCustomTags(toBackendScope(props.scope));
      await requirementsStore.refreshCurrentUserClientConfig();
      await loadTags();
    },
    { successText: "Reset to defaults.", errorText: "Failed to reset tags." }
  );
}

async function loadTags() {
  try {
    const data = await settingsService.getCustomTags();
    const intake = (data.intake || []).map((t) => ({ ...t, dirty: false }));
    const pa = (data.priorAuth || []).map((t) => ({ ...t, dirty: false }));
    intakeTags.value = intake;
    priorAuthTags.value = pa;
    intakeInitial.value = intake.map(({ key, label, color }) => ({ key, label, color }));
    priorAuthInitial.value = pa.map(({ key, label, color }) => ({ key, label, color }));
    loaded.value = true;
  } catch {
    error.value = "Failed to load tags.";
  }
}

watch(
  () => props.scope,
  () => {
    adding.value = false;
    error.value = "";
    successMsg.value = "";
  }
);

loadTags();
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.tag-mgmt {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tag-mgmt__toggle-row {
  flex-shrink: 0;
  margin-bottom: 14px;
}

.tag-mgmt__pill-toggle {
  display: inline-flex;
  background: $lh_color_snowy_white;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.tag-mgmt__pill {
  font-family: $lh_font_family_dm_sans;
  font-size: 13px;
  font-weight: 500;
  color: $lh_color_blue_gray;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 5px 14px;
  cursor: pointer;
  transition: all $lh_transition_fast;

  &:hover:not(.tag-mgmt__pill--active) {
    color: $lh_color_navy_dark;
  }
}

.tag-mgmt__pill--active {
  background: white;
  color: $lh_color_navy_dark;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.tag-mgmt__scroll {
  flex: 1;
  padding: 0 4px;
}

.tag-mgmt__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background-color $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }
}

.tag-mgmt__row--adding {
  background: $lh_color_snowy_white;
}

.tag-mgmt__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tag-mgmt__dot--clickable {
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
}

.tag-mgmt__dot-inner {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: block;
}

.tag-mgmt__input {
  flex: 1;
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_navy_dark;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  outline: none;
  background: transparent;
  transition: border-color $lh_transition_fast;

  &:focus {
    border-color: $lh_color_blue;
  }

  &:hover:not(:focus) {
    border-color: $lh_dash_border;
  }
}

.tag-mgmt__delete {
  color: $lh_color_blue_gray !important;
  opacity: 0;
  transition: opacity $lh_transition_fast;

  &--visible {
    opacity: 0.6;
  }

  &--visible:hover {
    color: $lh_color_red !important;
    opacity: 1;
  }
}

.tag-mgmt__palette {
  display: grid;
  grid-template-columns: repeat(8, 10px);
  gap: 8px;
  padding: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.tag-mgmt__palette-swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: border-color $lh_transition_fast;
}

.tag-mgmt__palette-swatch--active {
  border-color: $lh_color_navy_dark;
  box-shadow: 0 0 0 2px white, 0 0 0 4px currentColor;
}

.tag-mgmt__add-btn {
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_blue_gray;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: color $lh_transition_fast, background-color $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    background: $lh_color_snowy_white;
  }
}

.tag-mgmt__save-bar {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid $lh_dash_border;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 1;
}

.tag-mgmt__msg {
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
}

.tag-mgmt__msg--error {
  color: $lh_color_red;
}

.tag-mgmt__msg--success {
  color: $lh_color_green;
}

.tag-mgmt__empty {
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_blue_gray;
  padding: 12px;
}
</style>
